export interface ContractTaxLine {
  id: string
  includedInPrice: boolean
  isHostTax?: boolean
  percentage?: number
  amount?: number
  order?: number
  showInAdvertisedPrice: boolean
  appliesTo?: string[]
}

/**
 * One tax as it was actually applied. A snapshot rather than a figure: which rate was in force, what it
 * was charged on and whose money it is are all things a folio, a remittance run or a dispute needs, and
 * none are recoverable from an amount.
 */
export interface TaxCalculationDetail {
  id: string
  baseAmountForTax: number
  computedAmount: number
  addedToTotalAmount: number
  addedToAdvertisingAmount: number
  /**
   * The configured flat amount this was charged from, when the tax is a flat fee rather than a rate.
   *
   * Prefixed rather than named after the config field: a breakdown entry is routinely spread over the
   * tax it describes, and reusing `amount` would overwrite the configuration it records.
   */
  appliedFlatAmount?: number
  /** The configured rate this was charged at, when the tax is a percentage rather than a flat fee. */
  appliedPercentage?: number
  /** Whether the guest has already paid this inside the price, so it adds nothing to what is owed. */
  includedInPrice: boolean
  /** Whether this is the host's money — a gratuity or host fee — rather than a tax authority's. */
  isHostTax: boolean
}

export interface ContractAmountsOptions {
  /** Currency-aware rounding, injected so the currency table stays with the caller. Two decimals by default. */
  round?: (amount: number) => number
}

export interface CalculationResult {
  basePrice: number
  totalAmount: number
  advertisingAmount: number
  breakdown: TaxCalculationDetail[]
}

function roundToCents(value: number): number {
  return Math.round((value + Number.EPSILON) * 100) / 100
}

export function calculateContractAmounts(
  basePrice: number,
  taxes: ContractTaxLine[],
  { round = roundToCents }: ContractAmountsOptions = {},
): CalculationResult {
  const taxesSorted = [...taxes].sort(
    (a, b) =>
      (a.order ?? Number.MAX_SAFE_INTEGER) -
      (b.order ?? Number.MAX_SAFE_INTEGER),
  )

  let totalAmount = round(basePrice)
  let advertisingAmount = round(basePrice)

  const idToComputedAmount: Map<string, number> = new Map()
  const breakdown: TaxCalculationDetail[] = []

  for (const tax of taxesSorted) {
    const appliesToIds = tax.appliesTo ?? []

    let baseForTax = 0
    let computedAmount = 0

    const hasPercentage = typeof tax.percentage === 'number'
    const hasAmount = typeof tax.amount === 'number'

    if (hasPercentage && hasAmount) {
      // If both are provided incorrectly, prefer percentage per spec expectation (either-or).
    }

    if (hasPercentage) {
      const sumAppliesTo = appliesToIds.reduce((sum, id) => {
        const val = idToComputedAmount.get(id) ?? 0
        return sum + val
      }, 0)

      baseForTax = basePrice + sumAppliesTo
      if (tax.includedInPrice) {
        computedAmount =
          baseForTax *
          ((tax.percentage as number) / (100 + (tax.percentage as number)))
      } else {
        computedAmount = baseForTax * ((tax.percentage as number) / 100)
      }
    } else if (hasAmount) {
      // Flat amount ignores appliesTo for computation.
      baseForTax = 0
      computedAmount = tax.amount as number
    } else {
      // No percentage or amount; treat as zero contribution but still record.
      baseForTax = 0
      computedAmount = 0
    }

    idToComputedAmount.set(tax.id, computedAmount)

    const addedToTotalAmount = round(tax.includedInPrice ? 0 : computedAmount)
    const addedToAdvertisingAmount = round(
      !tax.includedInPrice && tax.showInAdvertisedPrice ? computedAmount : 0,
    )

    totalAmount = totalAmount + addedToTotalAmount
    advertisingAmount = advertisingAmount + addedToAdvertisingAmount

    breakdown.push({
      id: tax.id,
      // Rounded on the way out, unlike the value the compound base is built from: the reported base is
      // what a folio prints beside the amount, and an unrounded float there does not reproduce it.
      baseAmountForTax: round(baseForTax),
      computedAmount: round(computedAmount),
      addedToTotalAmount,
      addedToAdvertisingAmount,
      ...(hasPercentage
        ? { appliedPercentage: tax.percentage }
        : { appliedFlatAmount: tax.amount }),
      includedInPrice: Boolean(tax.includedInPrice),
      isHostTax: Boolean(tax.isHostTax),
    })
  }

  return {
    basePrice: round(basePrice),
    totalAmount: round(totalAmount),
    advertisingAmount: round(advertisingAmount),
    breakdown,
  }
}
